import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItemText,
  Checkbox,
  Divider,
  Stack,
  ListItem,
  IconButton,
} from '@mui/material';
import { useSearchUsers } from 'hooks/useUsers';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomText from 'components/common/CustomText/CustomText';
import { customTextFieldSlotProps, customTextFieldSx } from 'styles/customTextFieldStyles';
import CustomLabel from 'components/common/CustomLabel/CustomLabel';
import useStyles from './styles';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import useGlobalStyles from 'styles/globalStyles';
import EmptySearch from 'assets/emptySearch.png';
import { colors } from 'styles/colors';

export interface User {
  id?: string;
  invitee_name: string;
}

interface Props {
  title: string;
  value: User[];
  label?: string;
  required?: boolean;
  note?: string;
  onChange: (users: User[]) => void;
  placeholder?: string;
}

export default function SearchUsers({
  title,
  value,
  onChange,
  placeholder = '',
  label,
  required,
  note,
}: Props) {
  const styles = useStyles();
  const globalStyles = useGlobalStyles();

  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [apiSelected, setApiSelected] = useState<User[]>([]);
  const [manualUsers, setManualUsers] = useState<User[]>([]);

  const { refetch } = useSearchUsers(keyword, {
    enabled: false,
    onSuccess: (data: User[]) => setUsers(data),
  });

  useEffect(() => {
    if (keyword.trim()) {
      refetch();
    } else {
      setUsers([]);
    }
  }, [keyword]);

  const openModal = () => {
    setOpen(true);
    setKeyword('');
    setUsers([]);
    setApiSelected(value.filter(u => !!u.id));
    setManualUsers(value.filter(u => !u.id));
  };

  const toggleApiUser = (user: User) => {
    setApiSelected(prev =>
      prev.some(u => u.id === user.id)
        ? prev.filter(u => u.id !== user.id)
        : [...prev, user]
    );
  };

  const toggleManualUser = (name: string) => {
    setManualUsers(prev =>
      prev.some(u => u.invitee_name === name)
        ? prev.filter(u => u.invitee_name !== name)
        : [...prev, { invitee_name: name }]
    );
  };

  const allSelected = useMemo(
    () => [...apiSelected, ...manualUsers],
    [apiSelected, manualUsers]
  );

  const showManual =
    keyword.trim() &&
    !users.some(u => u.invitee_name === keyword) &&
    !manualUsers.some(u => u.invitee_name === keyword);

  const handleSave = () => {
    onChange(allSelected);
    setOpen(false);
  };

  return (
    <>
      {/* Input */}
      {label && (
        <CustomLabel
          label={label}
          note={note}
          required={required}
        />
      )}

      <TextField
        fullWidth
        size="small"
        value={
          value.length
            ? value.map(u => `(${u.invitee_name})`).join(', ')
            : ''
        }
        placeholder={placeholder}
        onClick={openModal}
        slotProps={{
          ...customTextFieldSlotProps,
          input: {
            ...customTextFieldSlotProps.input,
            readOnly: true,
          },
        }}
        sx={customTextFieldSx}
      />

      {/* Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: '0px 12px 48px rgba(0,0,0,0.2)',
              height: 600,
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <DialogTitle className={globalStyles.modalTitle}>
          {title}
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflow: 'hidden',
          }}
        >
          {/* Search */}
          <TextField
            fullWidth
            placeholder="Search users"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
          />

          {/* Results */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <List dense>

              {/* Manual entry */}
              {showManual && (
                <ListItem
                  onClick={() => toggleManualUser(keyword)}
                  className={styles.resultItem}
                >
                  <Checkbox checked={false} />
                  <ListItemText primary={keyword} />
                </ListItem>
              )}

              {/* Empty state */}
              {users.length === 0 && !keyword && (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  pt={4}
                >
                  <img src={EmptySearch} alt="search icon" />
                  <CustomText
                    text="No results to show"
                    fontSize={16}
                    color={colors.gray}
                  />
                </Box>
              )}

              {/* API users */}
              {users.map(user => {
                const checked = apiSelected.some(u => u.id === user.id);

                return (
                  <ListItem
                    key={user.id}
                    onClick={() => toggleApiUser(user)}
                    className={styles.resultItem}
                  >
                    <Checkbox checked={checked} />
                    <ListItemText primary={user.invitee_name} />
                  </ListItem>
                );
              })}
            </List>

            {/* Selected users */}
            {allSelected.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />

                <CustomText
                  text="Selected Users"
                  fontWeight={600}
                />

                <List dense>
                  {allSelected.map(user => (
                    <ListItem
                      key={(user.id || user.invitee_name) + '-selected'}
                      className={styles.resultItem}
                      secondaryAction={
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();

                            user.id
                              ? toggleApiUser(user)
                              : toggleManualUser(user.invitee_name);
                          }}
                          sx={{ padding: 0 }}
                        >
                          <RemoveCircleOutlineIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={user.invitee_name} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        </DialogContent>

        {/* Fixed footer */}
        <DialogActions sx={{ p: 2 }}>
          <CustomButton
            text="Save"
            fullWidth
            onClick={handleSave}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}